import React, { useState } from 'react';

import { path } from 'ramda';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { UserAccount } from '../../../../../../../../../../types';

import { findIndexFromFieldsArray } from 'components/shared/CRUD/utils/utils';

import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { useEditPageContext } from 'components/shared/CRUD/components/InnerPage/EditPageContext';
import withFieldArrayDrawer from 'components/shared/CRUD/components/InnerPage/components/withFieldArrayDrawer';
import { fieldArrayContext } from 'components/shared/CRUD/components/InnerPage/hooks/useFieldArrayContext';
import useRemoveUncommitted from 'components/shared/CRUD/components/InnerPage/hooks/useRemoveUncommitted';

import { useGetOptions } from '../../../../../../../../../../hooks/useGetOptions';
import { useGetUsers } from '../../../../../../../../../../queries';
import WithEditPageLayout, {
  WithEditPageLayoutProps
} from '../../../WithEditPageLayout';
import useColumnDefinitions from '../hooks/useColumnDefinitions';
import EditAccount from './EditAccount';

interface Props {
  isFetching: boolean;
  accounts: UserAccount[];
  setDeletedIds: React.Dispatch<any>;
}

const EditAccountDrawer = withFieldArrayDrawer(EditAccount, 'accounts');

const AccountsInner: React.FC<WithEditPageLayoutProps & Props> = ({
  isFetching,
  setDeletedIds
}) => {
  const { t } = useTranslation('common');

  const { data: users } = useGetUsers();

  const userOptions = useGetOptions(
    users ?? [],
    false,
    'accountId',
    ['nameFirstNames', 'nameLast'],
    '',
    ' '
  );
  const columns = useColumnDefinitions(users ?? []);

  const { isEditable } = useEditPageContext();

  const { control } = useFormContext();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    fields: accountsFieldArray,
    append,
    remove,
    update,
    replace
  } = useFieldArray({
    control: control,
    name: 'accounts'
  });

  const onAddNew = () => {
    // add a new empty record to the fieldArray
    append({
      isCommitted: false,
      valid: { from: null, to: null },
      accountId: null
    });
    setIsDrawerOpen(true);
  };

  const onDelete = (fieldIds) => {
    const index = findIndexFromFieldsArray(fieldIds[0], accountsFieldArray);
    const id = path<number>(['accountId'], accountsFieldArray[index]);
    setDeletedIds((prevState) => [...prevState, id]);

    remove(index);
  };

  useRemoveUncommitted(accountsFieldArray, isDrawerOpen, remove);

  const onCloseInnerView = () => {
    setIsDrawerOpen(false);
  };

  return (
    <fieldArrayContext.Provider
      value={{ fields: accountsFieldArray, append, remove, update, replace }}
    >
      <TableCRUDLayout
        FormComponent={(props) => (
          <EditAccountDrawer {...props} userOptions={userOptions} />
        )}
        rows={accountsFieldArray}
        loading={isFetching}
        columns={columns}
        getRowId={(row) => row.id}
        editable={isEditable}
        confirmationTitle={t('deleteDialog.title')}
        confirmationBody={t('deleteDialog.body')}
        onDeleteClick={onDelete}
        dynamicHeight={true}
        disableActions={!isEditable}
        shouldRenderToolBar={false}
        onAddClick={onAddNew}
        onClose={onCloseInnerView}
      />
    </fieldArrayContext.Provider>
  );
};

const AccountsInnerWithLayout = WithEditPageLayout(AccountsInner);
export default AccountsInnerWithLayout;
